import type { ExtractorsFile, GlobalDefaultsFile, OrchestrationStep, ProductType } from '@/types/bundle'

// The platform's own fixed, closed pipeline-stage sequence
// (legalflow-idp/app/core/orchestration.py's `STEP_ALLOWLIST`) - not real
// per-product customization. It's byte-identical across every real product
// type in every released bundle version, and `orchestration` is explicitly
// denylisted from firm-level override entirely
// (global_defaults.override_governance.denylist_prefixes includes
// "product_types.*.orchestration"). Safe to default silently for a new or
// duplicated product type and render read-only, exactly like the existing
// detail view already does (OrchestrationStepsSection) - never exposed as an
// editable field anywhere, including at creation.
export const PLATFORM_ORCHESTRATION_STEPS: OrchestrationStep[] = [
  { engine: 'validate_request' },
  { engine: 'identify_document_types' },
  { engine: 'extract_fields' },
  { engine: 'normalise' },
  { engine: 'resolve_lookups' },
  { engine: 'resolve_outputs' },
  { engine: 'build_response' },
]

// "+ New Product Type"'s starting shape - intentionally minimal (empty
// document requirements, filled in afterward via the detail view's existing
// "+ Add Document Type" modal) since it's a real unsaved draft the operator
// fills in, not a finished config. Limits default to the platform's own
// hard-cap ceiling so a new product starts at a safe, valid value the
// operator can lower, matching the helper text ("Platform maximum: X")
// already shown next to each limit field. extraction seeds the first real,
// enabled extractor if one exists (matches every real fixture, which has
// exactly one) - left unset otherwise, which ExtractionSection's own warning
// banner covers; output_resolution starts empty (filled in via its own "+
// Add Output Field"); case_data_requirements defaults to the exact shape
// every real fixture uses (an empty must_have_all, no must_have_any_of key).
export function buildEmptyProductType(
  hardCaps: GlobalDefaultsFile['hard_caps'],
  extractors: ExtractorsFile['extractors'],
): ProductType {
  const firstEnabledExtractorId = Object.entries(extractors).find(
    ([, extractor]) => extractor.enabled && extractor.type === 'bedrock_vision_extractor',
  )?.[0]

  return {
    mode: 'sync',
    document_requirements: {},
    case_data_requirements: { rules: { must_have_all: [] } },
    limits: {
      max_documents: hardCaps.max_documents,
      max_total_pages: hardCaps.max_total_pages,
      max_pdf_size_mb: hardCaps.max_pdf_size_mb,
      document_ingestion_mode: 'inline_only',
    },
    extraction: firstEnabledExtractorId ? { extractor_id: firstEnabledExtractorId } : undefined,
    output_resolution: {},
    orchestration: { steps: structuredClone(PLATFORM_ORCHESTRATION_STEPS) },
  }
}

// "Duplicate" clones every field from the source, including
// document_requirements and limits - unlike document types' response_tag,
// nothing inside a product type is per-instance-unique except the top-level
// id, which the New/Duplicate modal already validates for uniqueness.
// extraction.prompt_template_file is the one exception: it must never point
// two products at the same underlying prompt file, so it's cleared here -
// the list screen's onConfirm handler fetches the source's prompt content
// separately and seeds it into the new (unsaved) form, which writes it to a
// freshly-derived file only on Save (Design PRD 10's Duplicate bullet).
export function cloneForDuplicateProductType(source: ProductType): ProductType {
  const cloned = structuredClone(source)
  if (cloned.extraction) {
    cloned.extraction = { ...cloned.extraction, prompt_template_file: undefined }
  }
  return cloned
}

// Deterministic, never operator-visible or -editable - matches PRD 29's
// prompt_files.py filename validation (^[A-Za-z0-9_-]+\.txt$ exactly, no "/"
// at all, since the route is already scoped under prompts/). Real product
// type IDs (e.g. "memorandum_of_sale_data_extract") already satisfy this.
export function deriveProductBaselinePromptFilename(productTypeId: string): string {
  return `product_${productTypeId}_baseline.txt`
}
